/* eslint-disable react/prop-types */
import React, {
  useCallback,
  useState,
  useMemo,
  useContext,
  useEffect,
} from 'react';
import { BranchesOutlined } from '@ant-design/icons';

import WorkflowActionNode from './WorkflowActionNode';
import WorkflowHandle from './WorkflowHandle';
import FieldSelector from './selectors/FieldSelector';
import { updateData } from './workflowHelpers';
import FormWorkflowContext from './FormWorkflowContext';
import OnTraccrCheckbox from '../../common/inputs/OnTraccrCheckbox';

const HOVER_TEXT = `
The connected workflow will run on each of the selected options in the selected field.
`;

const DISABLED_TEXT = 'Add at least one Choose one or more field to the form to use this action.';

const ERROR_DETAILS = {
  content: (
    <div style={{ maxWidth: 300 }}>
      This step requires the <b>Select Field</b> to be configured.
    </div>
  ),
};

export default function WorkflowLogicalLoopNode({
  setElements,
  setDataMap,
  isDisplay,
  sections = [],
  setDisableNext,
} = {}) {
  return ({
    id,
    draggable,
    disabled,
    data = {},
  }) => {
    const {
      dataMap = {},
    } = useContext(FormWorkflowContext) ?? {};

    const {
      field: initialSelected,
      mapLabelToEquipment: shouldMapEquipmentLabel = false,
    } = dataMap[id] ?? data;
    const [selected, setSelected] = useState(initialSelected);
    const [
      equipmentLabelToggleValue,
      setEquipmentLabelToggleValue,
    ] = useState(shouldMapEquipmentLabel);

    useEffect(() => {
      if (!setDisableNext) return;
      setDisableNext((prev) => ({ ...prev, [id]: !selected }));
    }, [selected, setDisableNext, id]);

    const {
      dropdownFields,
      fieldMap,
    } = useMemo(() => {
      const fields = [];
      const map = {};

      sections.forEach(({ fields: sectionFields = [], name }) => {
        sectionFields.forEach((field) => {
          if (field.selectedType === 'dropdown') {
            map[field.id] = field;

            fields.push({
              id: field.id,
              label: `${name}: ${field.configProps.title}`,
            });
          }
        });
      });

      return {
        dropdownFields: fields,
        fieldMap: map,
      };
    }, [sections]);

    const isSelectedFieldEquipmentLabelDropdown = useCallback((value) => {
      const relevantField = fieldMap[value];
      if (!relevantField) return false;
      const {
        configProps: {
          dataType,
          subDataType = [],
        } = {},
        selectedType,
      } = relevantField;

      return selectedType === 'dropdown'
        && dataType === 'Labels'
        && subDataType?.length === 1
        && subDataType[0] === 'equipment';
    }, [fieldMap]);

    const shouldShowEquipmentLableToggle = useMemo(() => (
      isSelectedFieldEquipmentLabelDropdown(selected)
    ), [isSelectedFieldEquipmentLabelDropdown, selected]);

    const onChange = useCallback((newSelected) => {
      const selectedField = fieldMap[newSelected];
      const newPayload = {
        field: newSelected,
      };

      if (selectedField && !isSelectedFieldEquipmentLabelDropdown(newSelected)) {
        newPayload.mapLabelToEquipment = false;
      }

      setDataMap(updateData(id, newPayload));
      setSelected(newSelected);
    }, [id, setDataMap, isSelectedFieldEquipmentLabelDropdown]);

    const onToggle = useCallback((value) => {
      setDataMap(updateData(id, { mapLabelToEquipment: value }));
      setEquipmentLabelToggleValue(value);
    }, [id, setDataMap]);

    return (
      <WorkflowActionNode
        id={id}
        title={`Logical Loop ${disabled ? ' - DISABLED' : ''}`}
        Icon={BranchesOutlined}
        type="logicalLoop"
        draggable={draggable}
        onNodeUpdate={(...args) => {
          setElements(...args);
          if (setDisableNext) {
            setDisableNext((prev) => ({ ...prev, [id]: false }));
          }
        }}
        isDisplay={isDisplay || disabled}
        hover={disabled ? DISABLED_TEXT : HOVER_TEXT}
        style={disabled ? { opacity: 0.7 } : {}}
        className="workflow-logic-node"
        errorPopover={!selected ? ERROR_DETAILS : null}
      >
        {!draggable && (
        <div>
          <FieldSelector
            disabled={isDisplay}
            onChange={onChange}
            fields={dropdownFields}
            selected={selected}
          />
          { shouldShowEquipmentLableToggle && (
            <OnTraccrCheckbox
              checkboxStyle={{ maxWidth: '90%' }}
              style={{ marginTop: 5}}
              onChange={onToggle}
              defaultChecked={shouldMapEquipmentLabel}
              value={equipmentLabelToggleValue}
              label="Loop through associated equipment instead of labels"
              hoverNode={(
                <div style={{ width: 300 }}>
                  Check this box if you want to loop using the equipment
                  associated with the selected equipment labels.
                </div>
              )}
            />
          )}
          <WorkflowHandle type="target" position="top" disabled={isDisplay} />
          <WorkflowHandle
            type="source"
            position="bottom"
            disabled={isDisplay}
          />
        </div>
        )}
      </WorkflowActionNode>
    );
  };
}
